import { css, html, nothing } from "lit";
import { i18n } from "@lingui/core";
import WarpElement from "@warp-ds/elements-core";
import { Brand, Subvertical } from "../../constants.js";
import { LastSearchWithLink } from "../../models/lastsearch.js";
import { largeNumberFormatter } from "../../utils/formatters.js";
import { trackClickEvent } from "../tracking.js";
import { eventReference, TrackingEventMap } from "../../models/trackingEvent.js";
import { getLocaleByBrand } from "../../utils/getLocaleByBrand.js";
import { activateI18n } from "../../utils/i18n.js";

class LastSearches extends WarpElement {
    declare brand: Brand;
    declare subvertical: Subvertical;
    declare lastSearches: LastSearchWithLink[];
    declare basePath: string;
    declare trackingEventMap: TrackingEventMap;
    declare hidden: boolean;

    static properties = {
        subvertical: { type: Subvertical },
        brand: { type: String },
        lastSearches: { type: Array },
        basePath: { type: String },
        trackingEventMap: { type: Object },
        hidden: { type: Boolean, reflect: true },
    };

    static styles = [
        WarpElement.styles,
        css`
            :host([hidden]) {
                display: none;
            }
            @css-placeholder-8-21py;
        `,
    ];

    async fetchLastSearches() {
        try {
            const response = await fetch(`${this.basePath}/api/last-searches`);
            if (response.ok) {
                const data = await response.json();
                this.lastSearches = [...data]; // Force array reassignment to trigger reactivity
            }
        } catch (error) {
            console.error("Error fetching last searches:", error);
        }
    }

    constructor() {
        super();
        this.hidden = true;
        this.lastSearches = [];
    }

    async connectedCallback() {
        activateI18n();
        super.connectedCallback();
        await this.fetchLastSearches();
    }

    updated(changedProperties) {
        if (changedProperties.has("lastSearches")) {
            this.toggleVisibility();
        }
    }

    firstUpdated() {
        this.toggleVisibility();
    }

    toggleVisibility() {
        const hidden = !this.lastSearches || this.lastSearches?.length === 0;
        this.hidden = hidden;
    }

    render() {
        const hitsPrefix = i18n.t({
            id: "market-frontpage.last-searches.hits",
            message: "Hits",
        });

        return this.lastSearches?.length > 0
            ? html`<section
                  id="last-searches"
                  role="region"
                  aria-labelledby="last-searches-label"
                  data-lastsearch-delete-url="{{ deleteUrl }}"
                  data-device-type="desktop"
              >
                  <div class="flex items-center justify-between">
                      <h2 id="last-searches-label" class="t3 mb-0">
                          ${i18n.t({
                              id: "market-frontpage.last-searches.title",
                              message: "Previous searches",
                          })}
                      </h2>
                      <a
                          class="s-text-link bg-transparent"
                          href="?action=clearLastSearches"
                          @click="${() => trackClickEvent(this.trackingEventMap[eventReference.LAST_SEARCH_DELETE])}"
                      >
                          ${i18n.t({
                              id: "market-frontpage.last-searches.delete-all-button",
                              message: "Delete all searches",
                          })}
                      </a>
                  </div>

                  <p data-empty-message class="sr-only text-14">
                      ${i18n.t({
                          id: "market-frontpage.last-searches.description",
                          message: "As you search on {brand}, they will appear here.",
                          values: {
                              brand: this.brand,
                          },
                      })}
                  </p>
                  <ul data-links-list class="flex gap-16 mb-0 overflow-x-auto" style="scrollbar-width: none;">
                      ${this.lastSearches.map((item: LastSearchWithLink) => {
                          return html`
                              <li>
                                  <a
                                      href="${item.link}"
                                      class="h-32 s-bg-info-subtle px-12 py-8 whitespace-nowrap rounded-full flex items-center gap-8 my-16 s-text font-bold text-detail"
                                      @click="${() => trackClickEvent(item.trackingEvent)}"
                                  >
                                      ${item.description}
                                      <span class="ml-2 s-text-subtle font-normal">
                                          ${largeNumberFormatter(item.hits, getLocaleByBrand(this.brand))} ${hitsPrefix}
                                      </span>
                                  </a>
                              </li>
                          `;
                      })}
                  </ul>

                  <hr />
              </section>`
            : nothing;
    }
}

customElements.define("last-searches", LastSearches as unknown as CustomElementConstructor);
